import React, { useState, useEffect } from "react";
import { MakeApiCall } from "../../../../apis";
import { Upload, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Wrapper } from "./style";

const MyProfile = ({ setData, userData }) => {
  const Id = userData?.user?.user_data?.id;

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [editedProfile, setEditedProfile] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);

  useEffect(() => {
    const getProfileDetails = async () => {
      setLoading(true);
      const response = await MakeApiCall(
          `user/${Id}`,
          "get",
          null,
          true,
          {},
          userData?.token
      );

      if (response?.status) {
        setProfile(response?.data || {});
      } else {
        message.warning(response?.message);
      }
      setLoading(false);
    };

    getProfileDetails();
  }, []);

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
    }
    return isImage || Upload.LIST_IGNORE;
  };

  const handleProfilePictureChange = ({ file }) => {
    const isImage = file?.type?.startsWith("image/");
    if (!isImage) {
      message.error("Only image files are allowed!");
      return;
    }

    setNewImageFile(file.originFileObj);
    setImageChanged(true);

    const previewURL = URL.createObjectURL(file.originFileObj);
    setEditedProfile((prev) => ({
      ...prev,
      photo: previewURL,
    }));
  };

  const handleEditToggle = async () => {
    if (isEditing) {
      let hasChanges = JSON.stringify(profile) !== JSON.stringify(editedProfile);

      // Upload new image if changed
      if (imageChanged && newImageFile) {
        const formData = new FormData();
        formData.append("User[photo]", newImageFile);

        const imgRes = await MakeApiCall(
            `user/${Id}/update-image`,
            "post",
            formData,
            true,
            { "Content-Type": "multipart/form-data" },
            userData?.token
        );

        if (imgRes?.status) {
          message.success("Profile picture updated!");
          setProfile((prev) => ({
            ...prev,
            photo: imgRes?.data?.photo,
          }));
          hasChanges = true;
        } else {
          message.error("Image update failed.");
        }
      }

      // Update user details if changed
      if (hasChanges) {
        const response = await MakeApiCall(
            `user/${Id}`,
            "put",
            editedProfile,
            true,
            { "Content-Type": "application/json" },
            userData?.token
        );

        if (response?.status) {
          message.success("Profile updated successfully!");
          setProfile(editedProfile);
        } else {
          message.error(response?.message || "Failed to update profile.");
        }
      } else {
        message.info("No changes detected.");
      }

      // Reset states
      setImageChanged(false);
      setNewImageFile(null);
    } else {
      setEditedProfile({ ...profile });
    }

    setIsEditing(!isEditing);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const uploadButton = (
      <div>
        <PlusOutlined />
        <div style={{ marginTop: 8 }}>Upload</div>
      </div>
  );

  const currentPhoto = isEditing ? editedProfile?.photo : profile?.photo;

  if (!profile) return null;

  return (
      <Wrapper>
        <div className="row gy-5 g-xl-5 mt-5 justify-content-center">
          {/* Profile Card */}
          <div className="col-md-4 col-xxl-3">
            <div className="card mb-5 mb-xl-8 border-dashed border-danger shadow-sm">
              <div className="card-header border-0 justify-content-center">
                <h3 className="card-title fw-bolder text-danger">About Me</h3>
              </div>
              <div className="card-body text-center">
                <div
                    className="symbol symbol-circle symbol-100px border border-danger shadow-sm mb-4 mx-auto position-relative"
                    style={{ overflow: "hidden" }}
                >
                  <img
                      src={currentPhoto || ""}
                      alt="user"
                      className="w-100 h-100 object-fit-cover"
                      style={{ borderRadius: "50%" }}
                  />
                </div>
                <h4 className="fw-bold text-gray-800 mb-1">{profile.name}</h4>
                <div className="text-muted mb-1">{profile.email}</div>
                <div className="text-muted">{profile.contact_no || "N/A"}</div>
              </div>
            </div>
          </div>

          {/* Editable Form Card */}
          <div className="col-md-7 col-xxl-6">
            <div className="card shadow-sm mb-5 mb-xl-8">
              <div className="card-header border-0 d-flex justify-content-between align-items-center">
                <h3 className="card-title fw-bolder text-dark">Personal Information</h3>
                <button
                    className="btn btn-sm px-4 py-2 fw-bold"
                    style={{
                      backgroundColor: isEditing ? "#50cd89" : "#f45b68",
                      color: "#fff",
                      borderRadius: 6,
                      transition: "all 0.3s ease",
                    }}
                    onClick={handleEditToggle}
                >
                  {isEditing ? "Save Changes" : "Edit Profile"}
                </button>
              </div>

              <div className="card-body">
                <form>
                  {/* Profile Image Upload */}
                  <div className="mb-4 row align-items-center">
                    <label className="col-sm-4 col-form-label text-muted">Avatar</label>
                    <div className="col-sm-8">
                      <Upload
                          name="avatar"
                          listType="picture-card"
                          className="avatar-uploader"
                          showUploadList={false}
                          beforeUpload={beforeUpload}
                          onChange={handleProfilePictureChange}
                          disabled={!isEditing}
                      >
                        {currentPhoto ? (
                            <img
                                src={currentPhoto}
                                alt="avatar"
                                className="w-100"
                                style={{ borderRadius: "50%", maxWidth: "120px" }}
                            />
                        ) : (
                            uploadButton
                        )}
                      </Upload>
                    </div>
                  </div>

                  <hr className="my-4" />

                  {/* Fields */}
                  <FormRow
                      label="Name"
                      name="name"
                      value={isEditing ? editedProfile?.name : profile?.name}
                      readOnly={!isEditing}
                      onChange={handleChange}
                      isEditing={isEditing}
                  />
                  <FormRow
                      label="Email"
                      name="email"
                      value={isEditing ? editedProfile?.email : profile?.email}
                      readOnly={!isEditing}
                      onChange={handleChange}
                      isEditing={isEditing}
                  />
                  <FormRow
                      label="Contact"
                      name="contact_no"
                      value={isEditing ? editedProfile?.contact_no : profile?.contact_no || ""}
                      readOnly={!isEditing}
                      onChange={handleChange}
                      isEditing={isEditing}
                  />
                  <FormRow
                      label="Address"
                      name="address"
                      value={isEditing ? editedProfile?.address : profile?.address || ""}
                      readOnly={!isEditing}
                      onChange={handleChange}
                      isEditing={isEditing}
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
      </Wrapper>
  );
};

// Reusable form row component
const FormRow = ({ label, name, value, readOnly, onChange, isEditing }) => (
    <div className="mb-4 row">
      <label htmlFor={name} className="col-sm-4 col-form-label text-muted">
        {label}
      </label>
      <div className="col-sm-8">
        <input
            type="text"
            className={`form-control ${!isEditing ? "form-control-plaintext bg-transparent fw-semibold" : "form-control"}`}
            id={name}
            name={name}
            value={value}
            readOnly={readOnly}
            onChange={onChange}
            style={{ border: isEditing ? "1px solid #dee2e6" : "none" }}
        />
      </div>
    </div>
);

export default MyProfile;